import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../../apiclient/apiclient";
import Loader from "../../components/Loader";
import ThemedTable from "../../components/ThemedTable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, subDays } from "date-fns";
import {
  Download,
  Calendar,
  Search,
  RotateCcw,
  Package,
  Warehouse,
  TrendingUp,
  ShoppingCart,
  PackageCheck,
  CheckCircle2,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";

// Searchable Product Dropdown
const SearchableProductSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Search and select product...",
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const selected = options.find((o) => o._id === value);
    if (selected) {
      setQuery(
        `${selected.name} ${selected.quantityValue ? `(${selected.quantityValue}${selected.quantityUnit || ""})` : ""}`
      );
    } else {
      setQuery("");
    }
  }, [value, options]);

  const filtered = options.filter((o) => {
    const brandName = o.brand?.name || o.brandName || "";
    const fullName = `${o.name} ${brandName} ${o.quantityValue || ""} ${o.quantityUnit || ""}`.toLowerCase();
    return fullName.includes(query.toLowerCase());
  });

  return (
    <div className="relative w-full" ref={ref}>
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full shadow-sm text-slate-700 font-semibold pr-8"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
      />
      {query ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange("");
            setQuery("");
          }}
          className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600 transition p-0.5"
          title="Clear product"
        >
          <X size={15} />
        </button>
      ) : (
        <span
          className="absolute right-3 top-2.5 text-slate-400 text-[10px] pointer-events-none cursor-pointer"
          onClick={() => setOpen(!open)}
        >
          ▼
        </span>
      )}

      {open && (
        <ul className="absolute w-full bg-white border rounded-lg mt-1 max-h-60 overflow-auto shadow-xl z-50 divide-y divide-slate-50">
          {filtered.length === 0 ? (
            <li className="p-3 text-sm text-gray-500 text-center">No matching products found</li>
          ) : (
            filtered.map((p) => (
              <li
                key={p._id}
                className="p-2.5 hover:bg-blue-50/70 cursor-pointer text-left transition flex justify-between items-center"
                onClick={() => {
                  onChange(p._id);
                  setQuery(`${p.name} (${p.quantityValue || ""}${p.quantityUnit || ""})`);
                  setOpen(false);
                }}
              >
                <div>
                  <p className="text-sm font-bold text-slate-800">{p.name}</p>
                  <p className="text-[11px] text-slate-500 font-medium">
                    Brand: {p.brand?.name || p.brandName || "N/A"} • Variant: {p.quantityValue} {p.quantityUnit}
                  </p>
                </div>
                <span className="text-xs font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                  Stock: {p.totalQuantity ?? p.stock ?? 0}
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

const ProductStockLedger = () => {
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("all");
  const [fromDate, setFromDate] = useState(subDays(new Date(), 7));
  const [toDate, setToDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState("sales"); // "sales" or "inventory"
  const [tableSearch, setTableSearch] = useState("");

  // Demo generated ledger data
  const [ledgerData, setLedgerData] = useState(null);

  /* -------------------------------------------------------------
     1. Load Products & Warehouses for Dropdowns
  -------------------------------------------------------------- */
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        // Load products
        const prodRes = await apiClient.get("/products?lite=true");
        const prodList = prodRes.data?.data || prodRes.data || [];
        setProducts(prodList);

        // Load warehouses / locations
        const locRes = await apiClient.get("/locations");
        const locList = locRes.data?.locations || locRes.data || [];
        setWarehouses(locList);
      } catch (err) {
        console.warn("Using fallback demo catalog", err);
        const demoFallbackProds = [
          { _id: "prod_1", name: "Whey Protein Isolate 90%", quantityValue: 1, quantityUnit: "kg", brand: { name: "MuscleBlaze" }, totalQuantity: 255 },
          { _id: "prod_2", name: "Creatine Monohydrate Micronized", quantityValue: 250, quantityUnit: "g", brand: { name: "Optimum Nutrition" }, totalQuantity: 140 },
          { _id: "prod_3", name: "Alpino Chocolate Peanut Butter", quantityValue: 2.25, quantityUnit: "kg", brand: { name: "Alpino" }, totalQuantity: 88 },
        ];
        setProducts(demoFallbackProds);
        setWarehouses([
          { _id: "wh_1", name: "OLD SHOP DD PURAM" },
          { _id: "wh_2", name: "MAIN GODOWN" },
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  /* -------------------------------------------------------------
     2. Generate Ledger Statement (Live Backend API)
  -------------------------------------------------------------- */
  const generateStatement = async () => {
    if (!selectedProduct) {
      toast.error("Please select a product first");
      return;
    }

    setLoading(true);

    try {
      const formattedFrom = format(fromDate, "yyyy-MM-dd");
      const formattedTo = format(toDate, "yyyy-MM-dd");

      const params = {
        productId: selectedProduct,
        start: formattedFrom,
        end: formattedTo,
      };
      if (selectedWarehouse && selectedWarehouse !== "all") {
        params.warehouseId = selectedWarehouse;
      }

      const res = await apiClient.get("/product-stock-ledger", { params });

      if (res.data?.success) {
        setLedgerData({
          product: res.data.product,
          openingStock: res.data.openingStock,
          totalAdded: res.data.totalAdded,
          totalSold: res.data.totalSold,
          totalDeductions: res.data.totalDeductions || 0,
          totalTransfersOut: res.data.totalTransfersOut || 0,
          closingStock: res.data.closingStock,
          transactions: res.data.transactions || [],
        });
      } else {
        toast.error(res.data?.message || "Failed to load stock ledger");
      }
    } catch (err) {
      console.error("Stock Ledger API Error:", err);
      toast.error(err.response?.data?.message || "Error fetching product stock ledger");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------------
     Reset Filters
  -------------------------------------------------------------- */
  const handleResetFilters = () => {
    setSelectedProduct("");
    setSelectedWarehouse("all");
    setFromDate(subDays(new Date(), 7));
    setToDate(new Date());
    setLedgerData(null);
    setTableSearch("");
    setActiveTab("sales");
  };

  /* -------------------------------------------------------------
     3. Separate Sales & Inventory Additions Lists
  -------------------------------------------------------------- */
  const {
    salesList,
    inventoryList,
    salesTotalUnits,
    inventoryAddedUnits,
    inventoryDeductedUnits,
    inventoryTransfersUnits,
    inventoryWriteOffUnits,
    inventoryRestockUnits,
  } = useMemo(() => {
    const txs = ledgerData?.transactions || [];
    const sales = [];
    const inventory = [];

    txs.forEach((t) => {
      // Sales transactions: order, pos, retail, or any customer sales outflow
      if (t.type === "order" || t.type === "pos" || t.type === "retail" || (t.qtyOut > 0 && !t.movementType)) {
        sales.push(t);
      } else {
        // Inventory transactions: restock, return, transfer, deduction/write-off
        inventory.push(t);
      }
    });

    const sUnits = sales.reduce((sum, t) => sum + (t.qtyOut || 0), 0);
    const inUnits = inventory.reduce((sum, t) => sum + (t.qtyIn || 0), 0);
    const outUnits = inventory.reduce((sum, t) => sum + (t.qtyOut || 0), 0);

    let trfUnits = 0;
    let writeOffs = 0;
    let restocks = 0;

    inventory.forEach((t) => {
      if (t.movementType === "transfer" || t.type === "transfer") {
        if (t.qtyIn > 0) trfUnits += Number(t.qtyIn || 0);
      } else if (t.movementType === "deduction" || t.movementType === "dispose" || t.typeLabel === "Stock Write-off") {
        writeOffs += Number(t.qtyOut || 0);
      } else if (t.qtyIn > 0) {
        restocks += Number(t.qtyIn || 0);
      } else if (t.qtyOut > 0) {
        writeOffs += Number(t.qtyOut || 0);
      }
    });

    return {
      salesList: sales,
      inventoryList: inventory,
      salesTotalUnits: sUnits,
      inventoryAddedUnits: inUnits,
      inventoryDeductedUnits: outUnits,
      inventoryTransfersUnits: trfUnits,
      inventoryWriteOffUnits: writeOffs,
      inventoryRestockUnits: restocks,
    };
  }, [ledgerData]);

  // Filtered Sales with Search
  const filteredSales = useMemo(() => {
    if (!tableSearch.trim()) return salesList;
    const q = tableSearch.toLowerCase();
    return salesList.filter((t) => {
      const matchRef = t.referenceId?.toLowerCase().includes(q);
      const matchParty = t.party?.toLowerCase().includes(q);
      const matchWarehouse = t.warehouse?.toLowerCase().includes(q);
      const matchType = t.typeLabel?.toLowerCase().includes(q);
      const matchPhone = t.clientPhone?.toLowerCase().includes(q);
      return matchRef || matchParty || matchWarehouse || matchType || matchPhone;
    });
  }, [salesList, tableSearch]);

  // Filtered Inventory with Search
  const filteredInventory = useMemo(() => {
    if (!tableSearch.trim()) return inventoryList;
    const q = tableSearch.toLowerCase();
    return inventoryList.filter((t) => {
      const matchRef = t.referenceId?.toLowerCase().includes(q);
      const matchParty = t.party?.toLowerCase().includes(q);
      const matchWarehouse = t.warehouse?.toLowerCase().includes(q);
      const matchType = t.typeLabel?.toLowerCase().includes(q);
      const matchBatch = t.batchId?.toLowerCase().includes(q);
      const matchUpdated = t.updatedBy?.toLowerCase().includes(q);
      const matchNotes = t.notes?.toLowerCase().includes(q);
      return matchRef || matchParty || matchWarehouse || matchType || matchBatch || matchUpdated || matchNotes;
    });
  }, [inventoryList, tableSearch]);

  /* -------------------------------------------------------------
     4. Export Ledger to Multi-Sheet Excel
  -------------------------------------------------------------- */
  const handleExportExcel = async () => {
    if (!ledgerData) return;

    try {
      const workbook = new ExcelJS.Workbook();

      // ================= SHEET 1: SALES DETAILS =================
      const salesSheet = workbook.addWorksheet("Sales Details");

      salesSheet.mergeCells("A1:K1");
      const sTitle = salesSheet.getCell("A1");
      sTitle.value = `SALES AUDIT: ${ledgerData.product.name.toUpperCase()}`;
      sTitle.font = { size: 14, bold: true, color: { argb: "FFFFFFFF" } };
      sTitle.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E293B" } };
      sTitle.alignment = { horizontal: "center", vertical: "middle" };
      salesSheet.getRow(1).height = 32;

      salesSheet.mergeCells("A2:K2");
      const sSub = salesSheet.getCell("A2");
      sSub.value = `Period: ${format(fromDate, "dd/MM/yyyy")} to ${format(toDate, "dd/MM/yyyy")}  |  Total Sales: ${salesList.length} Orders  |  Units Sold: ${salesTotalUnits} Units`;
      sSub.font = { size: 10, italic: true, color: { argb: "FFFFFFFF" } };
      sSub.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E293B" } };
      sSub.alignment = { horizontal: "center", vertical: "middle" };
      salesSheet.getRow(2).height = 22;

      salesSheet.addRow([]);

      const sHeader = salesSheet.addRow([
        "#",
        "Date & Time",
        "Order ID",
        "Client / Customer",
        "Channel / Type",
        "Warehouse",
        "Qty Sold",
        "Rate (₹)",
        "Line Total (₹)",
        "Status",
        "Balance After",
      ]);
      sHeader.height = 24;
      sHeader.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E293B" } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });

      filteredSales.forEach((t, idx) => {
        const qtySold = Number(t.qtyOut) || 0;
        const rate = t.unitPrice && !isNaN(Number(t.unitPrice)) ? Number(t.unitPrice) : "-";
        const lineTot = t.lineTotal && !isNaN(Number(t.lineTotal)) ? Number(t.lineTotal) : "-";

        const r = salesSheet.addRow([
          idx + 1,
          format(new Date(t.date), "dd/MM/yyyy HH:mm"),
          t.referenceId,
          t.party,
          t.typeLabel,
          t.warehouse,
          qtySold,
          rate,
          lineTot,
          `${t.orderStatus || "completed"} (${t.paymentStatus || "paid"})`,
          t.runningBalance !== undefined && t.runningBalance !== null ? Number(t.runningBalance) : "-",
        ]);
        r.getCell(1).alignment = { horizontal: "center" };
        r.getCell(7).alignment = { horizontal: "center" };
        if (typeof rate === "number") {
          r.getCell(8).alignment = { horizontal: "right" };
          r.getCell(8).numFmt = "#,##0";
        } else {
          r.getCell(8).alignment = { horizontal: "center" };
        }
        if (typeof lineTot === "number") {
          r.getCell(9).alignment = { horizontal: "right" };
          r.getCell(9).numFmt = "#,##0";
        } else {
          r.getCell(9).alignment = { horizontal: "center" };
        }
        r.getCell(11).alignment = { horizontal: "center" };
      });

      // Total Sales Summary Row in Excel
      const totalQtySold = filteredSales.reduce((sum, t) => sum + (Number(t.qtyOut) || 0), 0);
      const totalRevenue = filteredSales.reduce((sum, t) => sum + (Number(t.lineTotal) || 0), 0);

      const totalRow = salesSheet.addRow([
        `TOTAL (${filteredSales.length} Orders)`,
        "",
        "",
        "",
        "",
        "",
        totalQtySold,
        "",
        totalRevenue,
        "",
        "",
      ]);
      totalRow.height = 26;

      const totalRowIdx = totalRow.number;
      salesSheet.mergeCells(`A${totalRowIdx}:F${totalRowIdx}`);

      totalRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { bold: true, size: 11, color: { argb: "FF0F172A" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEFF6FF" },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF93C5FD" } },
          bottom: { style: "double", color: { argb: "FF93C5FD" } },
        };
      });

      const totalLabelCell = salesSheet.getCell(`A${totalRowIdx}`);
      totalLabelCell.alignment = { horizontal: "center", vertical: "middle" };

      const totalQtyCell = totalRow.getCell(7);
      totalQtyCell.alignment = { horizontal: "center", vertical: "middle" };
      totalQtyCell.numFmt = "#,##0";

      const totalAmountCell = totalRow.getCell(9);
      totalAmountCell.alignment = { horizontal: "right", vertical: "middle" };
      totalAmountCell.numFmt = "#,##0";

      salesSheet.columns.forEach((col) => {
        col.width = 20;
      });

      // ================= SHEET 2: INVENTORY MOVEMENTS =================
      const invSheet = workbook.addWorksheet("Inventory Movements");

      invSheet.mergeCells("A1:J1");
      const iTitle = invSheet.getCell("A1");
      iTitle.value = `INVENTORY & STOCK MOVEMENTS: ${ledgerData.product.name.toUpperCase()}`;
      iTitle.font = { size: 14, bold: true, color: { argb: "FFFFFFFF" } };
      iTitle.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF059669" } };
      iTitle.alignment = { horizontal: "center", vertical: "middle" };
      invSheet.getRow(1).height = 32;

      invSheet.mergeCells("A2:J2");
      const iSub = invSheet.getCell("A2");
      const invSummaryParts = [];
      if (inventoryTransfersUnits > 0) {
        invSummaryParts.push(`Transfers: ${inventoryTransfersUnits} Units Moved`);
      }
      if (inventoryRestockUnits > 0) {
        invSummaryParts.push(`Restocks: +${inventoryRestockUnits} Units`);
      }
      if (inventoryWriteOffUnits > 0) {
        invSummaryParts.push(`Write-offs: -${inventoryWriteOffUnits} Unit${inventoryWriteOffUnits > 1 ? "s" : ""}`);
      }
      if (invSummaryParts.length === 0) {
        if (inventoryAddedUnits > 0) invSummaryParts.push(`Inward: +${inventoryAddedUnits} Units`);
        if (inventoryDeductedUnits > 0) invSummaryParts.push(`Deductions: -${inventoryDeductedUnits} Units`);
      }
      const invSummaryText = invSummaryParts.join("  |  ");

      iSub.value = `Period: ${format(fromDate, "dd/MM/yyyy")} to ${format(toDate, "dd/MM/yyyy")}  |  Total Entries: ${inventoryList.length}  |  ${invSummaryText}`;
      iSub.font = { size: 10, italic: true, color: { argb: "FFFFFFFF" } };
      iSub.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E293B" } };
      iSub.alignment = { horizontal: "center", vertical: "middle" };
      invSheet.getRow(2).height = 22;

      invSheet.addRow([]);

      const iHeader = invSheet.addRow([
        "#",
        "Date & Time",
        "Movement Type",
        "Batch / Ref ID",
        "Godown / Warehouse",
        "Qty Movement",
        "Batch & Expiry",
        "Updated By",
        "Remarks / Notes",
        "Balance After",
      ]);
      iHeader.height = 24;
      iHeader.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF059669" } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });

      filteredInventory.forEach((t, idx) => {
        const r = invSheet.addRow([
          idx + 1,
          format(new Date(t.date), "dd/MM/yyyy HH:mm"),
          t.typeLabel,
          t.referenceId,
          t.warehouse,
          t.qtyIn > 0 ? `+${t.qtyIn}` : `-${t.qtyOut}`,
          t.expiryDate ? format(new Date(t.expiryDate), "dd/MM/yyyy") : "-",
          t.updatedBy || "-",
          t.notes || t.party || "-",
          t.runningBalance,
        ]);
        r.getCell(6).alignment = { horizontal: "center" };
        r.getCell(10).alignment = { horizontal: "right" };
      });

      // Total Inventory Movements Summary Row in Excel
      const netInvChange = inventoryAddedUnits - inventoryDeductedUnits;
      const totalInvRow = invSheet.addRow([
        `TOTAL (${filteredInventory.length} Entries)`,
        "",
        "",
        "",
        "",
        `Net: ${netInvChange > 0 ? "+" : ""}${netInvChange} Units`,
        "",
        "",
        "",
        `Closing: ${ledgerData.closingStock} Units`,
      ]);
      totalInvRow.height = 26;

      const totalInvIdx = totalInvRow.number;
      invSheet.mergeCells(`A${totalInvIdx}:E${totalInvIdx}`);

      totalInvRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { bold: true, size: 11, color: { argb: "FF0F172A" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFECFDF5" },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF6EE7B7" } },
          bottom: { style: "double", color: { argb: "FF6EE7B7" } },
        };
      });

      const totalInvLabelCell = invSheet.getCell(`A${totalInvIdx}`);
      totalInvLabelCell.alignment = { horizontal: "center", vertical: "middle" };

      const totalQtyMoveCell = totalInvRow.getCell(6);
      totalQtyMoveCell.alignment = { horizontal: "center", vertical: "middle" };

      const totalClosingCell = totalInvRow.getCell(10);
      totalClosingCell.alignment = { horizontal: "right", vertical: "middle" };

      invSheet.columns.forEach((col) => {
        col.width = 20;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Stock_Ledger_${ledgerData.product.name.replace(/[^a-zA-Z0-9]/g, "_")}_${format(fromDate, "ddMMyyyy")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to export Excel", err);
      toast.error("Failed to download ledger report");
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* ================= HEADER ================= */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Product Stock Ledger</h1>
          <p className="text-sm text-slate-500 mt-1">
            Audit opening inventory, inward stock additions, sales deductions, and closing stock
          </p>
        </div>
      </div>

      {/* ================= CONTROL BAR (LIGHT BLUE SECTION) ================= */}
      <div className="border-t border-b bg-[#EFF6FF] px-4 py-3 sm:px-6 shadow-xs">
        <div className="flex flex-col lg:flex-row items-center gap-3">
          {/* PRODUCT (FIRST & EXPANDED) */}
          <div className="flex items-center gap-2 w-full lg:flex-1">
            <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
              Product
            </label>
            <div className="w-full">
              <SearchableProductSelect
                options={products}
                value={selectedProduct}
                onChange={(id) => setSelectedProduct(id)}
                placeholder="Search and select product..."
              />
            </div>
          </div>

          {/* FROM */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
              From
            </label>
            <DatePicker
              selected={fromDate}
              onChange={(date) => setFromDate(date || new Date())}
              dateFormat="dd/MM/yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              maxDate={toDate}
              className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:w-36 shadow-sm font-medium text-center"
            />
          </div>

          {/* TO */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
              To
            </label>
            <DatePicker
              selected={toDate}
              onChange={(date) => setToDate(date || new Date())}
              dateFormat="dd/MM/yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              minDate={fromDate}
              maxDate={new Date()}
              className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:w-36 shadow-sm font-medium text-center"
            />
          </div>

          {/* ACTIONS */}
          <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
            {/* VIEW REPORT */}
            <button
              onClick={generateStatement}
              disabled={loading}
              className="w-9 h-9 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all shadow-sm hover:shadow active:scale-95 flex items-center justify-center shrink-0 disabled:opacity-50 cursor-pointer"
              title="View Report"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Search size={18} />
              )}
            </button>

            {/* RESET BUTTON */}
            <button
              onClick={handleResetFilters}
              disabled={loading}
              className="w-9 h-9 border border-slate-200 bg-white text-slate-600 rounded-lg hover:bg-slate-100 hover:text-slate-900 transition-all shadow-sm hover:shadow active:scale-95 flex items-center justify-center shrink-0 disabled:opacity-50 cursor-pointer"
              title="Reset Filters"
            >
              <RotateCcw size={16} />
            </button>

            {/* DOWNLOAD EXCEL */}
            <button
              onClick={handleExportExcel}
              disabled={!ledgerData || loading}
              className="w-9 h-9 bg-[#172554] text-white rounded-lg hover:bg-[#1e2f6a] transition-all shadow-sm hover:shadow active:scale-95 flex items-center justify-center shrink-0 disabled:opacity-50 cursor-pointer"
              title="Download Excel"
            >
              <Download size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* ================= MAIN CONTENT: EMPTY STATE OR LEDGER DASHBOARD ================= */}
      {!ledgerData && !loading ? (
        <div className="py-24 text-center bg-white border-b border-slate-200">
          <div className="w-16 h-16 bg-blue-50 text-[#172554] rounded-2xl flex items-center justify-center mx-auto mb-4 border border-blue-100 shadow-sm">
            <Package size={28} />
          </div>
          <h3 className="text-base font-bold text-slate-800">
            Select a Product to View Stock Ledger
          </h3>
          <p className="text-xs text-slate-500 mt-1.5 max-w-sm mx-auto">
            Choose a product from the control bar above and select your date range to audit opening inventory, inward stock additions, sales, and closing stock.
          </p>
        </div>
      ) : (
        <>
          {/* ================= 4 CORE KPI METRIC CARDS (SALES REPORT GRADIENT TILES) ================= */}
          {ledgerData && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              {/* Card 1: Opening Stock */}
              <div className="relative px-4 py-4 text-white overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 border-e border-b flex flex-col justify-between min-h-[135px]">
                <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40 pointer-events-none"></div>
                <div>
                  <p className="text-xs sm:text-sm opacity-85 relative z-10 font-semibold uppercase tracking-wider">
                    1. Opening Stock
                  </p>
                  <h2 className="text-2xl sm:text-3xl font-bold mt-1.5 relative z-10">
                    {ledgerData.openingStock} <span className="text-xs sm:text-sm font-normal opacity-80">Units</span>
                  </h2>
                </div>
                <p className="text-xs opacity-85 mt-2 relative z-10 font-medium">
                  As on {format(fromDate, "dd MMM yyyy, 00:00")}
                </p>
              </div>

              {/* Card 2: Total Added */}
              <div className="relative px-4 py-4 text-white overflow-hidden bg-gradient-to-br from-emerald-500 to-green-600 border-e border-b flex flex-col justify-between min-h-[135px]">
                <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40 pointer-events-none"></div>
                <div>
                  <div className="flex items-center justify-between gap-1 relative z-10">
                    <p className="text-xs sm:text-sm opacity-85 font-semibold uppercase tracking-wider whitespace-nowrap">
                      2. Total Added
                    </p>
                    {ledgerData.totalDeductions > 0 && (
                      <span className="bg-white/20 backdrop-blur-xs px-1.5 py-0.5 rounded text-[10px] font-bold text-white uppercase tracking-wider shrink-0">
                        Write-offs: -{ledgerData.totalDeductions}
                      </span>
                    )}
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold mt-1.5 relative z-10">
                    +{ledgerData.totalAdded} <span className="text-xs sm:text-sm font-normal opacity-80">Units</span>
                  </h2>
                </div>
                <p className="text-xs opacity-85 mt-2 relative z-10 font-medium truncate">
                  Restocks, Transfers In & Returns
                </p>
              </div>

              {/* Card 3: Total Sold */}
              <div className="relative px-4 py-4 text-white overflow-hidden bg-gradient-to-br from-orange-500 to-amber-600 border-e border-b flex flex-col justify-between min-h-[135px]">
                <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40 pointer-events-none"></div>
                <div>
                  <p className="text-xs sm:text-sm opacity-85 relative z-10 font-semibold uppercase tracking-wider">
                    3. Total Sold
                  </p>
                  <h2 className="text-2xl sm:text-3xl font-bold mt-1.5 relative z-10">
                    -{ledgerData.totalSold} <span className="text-xs sm:text-sm font-normal opacity-80">Units</span>
                  </h2>
                </div>
                <p className="text-xs opacity-85 mt-2 relative z-10 font-medium truncate">
                  Orders Dispatched & POS Retail
                </p>
              </div>

              {/* Card 4: Closing Stock */}
              <div className="relative px-4 py-4 text-white overflow-hidden bg-gradient-to-br from-cyan-500 to-sky-600 border-b flex flex-col justify-between min-h-[135px]">
                <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40 pointer-events-none"></div>
                <div>
                  <p className="text-xs sm:text-sm opacity-85 font-semibold uppercase tracking-wider relative z-10">
                    4. Closing Stock
                  </p>
                  <h2 className="text-2xl sm:text-3xl font-bold mt-1.5 truncate relative z-10">
                    {ledgerData.closingStock} <span className="text-xs sm:text-sm font-normal opacity-80">Units</span>
                  </h2>
                </div>
                <p className="text-[11px] opacity-90 mt-2 relative z-10 font-medium tracking-tight leading-snug">
                  {ledgerData.openingStock} (Open)
                  {ledgerData.totalAdded > 0 ? ` + ${ledgerData.totalAdded}` : ""}
                  {ledgerData.totalSold > 0 ? ` - ${ledgerData.totalSold}` : ""}
                  {ledgerData.totalDeductions > 0 ? ` - ${ledgerData.totalDeductions} (Write-off)` : ""}
                  {ledgerData.totalTransfersOut > 0 ? ` - ${ledgerData.totalTransfersOut} (Trf Out)` : ""}
                  {" = "}
                  <span className="font-bold underline underline-offset-2">{ledgerData.closingStock}</span>
                </p>
              </div>
            </div>
          )}

      {/* ================= TABLE CONTROL PANEL (CAPSULE TOGGLE SWITCH & SEARCH) ================= */}
      <div className="w-full bg-white border-b border-slate-200 px-4 sm:px-6 py-3.5 flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Custom Capsule Toggle Switch */}
        <div className="flex items-center gap-3.5 select-none self-start md:self-auto">
          {/* Sales Details Text Label */}
          <span
            onClick={() => setActiveTab("sales")}
            className={`text-sm cursor-pointer transition-all duration-200 ${
              activeTab === "sales"
                ? "font-bold text-slate-900"
                : "text-slate-400 hover:text-slate-600 font-semibold"
            }`}
          >
            Sales Details ({filteredSales.length})
          </span>

          {/* Custom Capsule Toggle Switch */}
          <div
            onClick={() => setActiveTab(activeTab === "sales" ? "inventory" : "sales")}
            className="w-[50px] h-[26px] bg-slate-200/80 hover:bg-slate-200 rounded-full p-[3px] cursor-pointer transition-all duration-300 flex items-center relative border border-slate-300/40 shrink-0"
            title="Switch table view"
          >
            <div
              className={`w-5 h-5 bg-orange-500 rounded-full shadow transition-all duration-300 transform ${
                activeTab === "inventory" ? "translate-x-6" : "translate-x-0"
              }`}
            />
          </div>

          {/* Inventory Movements Text Label */}
          <span
            onClick={() => setActiveTab("inventory")}
            className={`text-sm cursor-pointer transition-all duration-200 ${
              activeTab === "inventory"
                ? "font-bold text-slate-900"
                : "text-slate-400 hover:text-slate-600 font-semibold"
            }`}
          >
            Inventory & Stock Adjustments ({filteredInventory.length})
          </span>
        </div>

        {/* Quick Search */}
        <div className="relative w-full md:w-72 shrink-0">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="w-4 h-4 text-slate-400" />
          </span>
          <input
            type="text"
            placeholder={
              activeTab === "sales"
                ? "Search orders, client, invoice..."
                : "Search batch, godown, remarks..."
            }
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            className="w-full h-9 pl-9 pr-8 text-xs bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-lg outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition shadow-2xs font-medium text-slate-700 placeholder:text-slate-400"
          />
          {tableSearch && (
            <button
              onClick={() => setTableSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition cursor-pointer"
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ================= DEDICATED TABLES ================= */}
      <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-200 bg-white border-b border-slate-200">
        {activeTab === "sales" ? (
          /* ================= 1. DEDICATED SALES TABLE (SALES OVERVIEW STYLE) ================= */
          <ThemedTable className="w-full text-[13px]">
            <thead>
              <tr className="bg-[#172554] text-white uppercase tracking-wider text-[11px]">
                <th className="p-3 text-center w-12 whitespace-nowrap">#</th>
                <th className="p-3 text-left whitespace-nowrap">Order Details</th>
                <th className="p-3 text-left whitespace-nowrap">Client Name</th>
                <th className="p-3 text-left whitespace-nowrap">Products Details</th>
                <th className="p-3 text-right whitespace-nowrap">Billing Details</th>
                <th className="p-3 text-center whitespace-nowrap">Statuses</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium text-slate-700 text-xs">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <Loader text="Loading sales records..." />
                  </td>
                </tr>
              ) : filteredSales.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-14 text-center text-slate-400 italic">
                    No sales orders recorded for this period.
                  </td>
                </tr>
              ) : (
                filteredSales.map((t, index) => {
                  return (
                    <tr key={t.id} className="hover:bg-slate-50/80 transition">
                      <td className="py-3 px-4 text-center text-slate-400 font-mono whitespace-nowrap">
                        {index + 1}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/orders?search=${t.referenceId}`}
                            className="font-bold text-[#172554] hover:underline block text-sm"
                            title="View order"
                          >
                            {t.referenceId}
                          </Link>
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                              t.type === "pos"
                                ? "bg-purple-100 text-purple-700"
                                : t.type === "retail"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-amber-100 text-amber-800"
                            }`}
                          >
                            {t.typeLabel}
                          </span>
                        </div>
                        <span className="text-[11px] text-slate-400 block mt-1 font-medium">
                          {format(new Date(t.date), "dd/MM/yyyy, hh:mm a")}
                        </span>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className="font-bold text-slate-800 text-sm block">{t.party}</span>
                        {t.clientPhone ? (
                          <span className="text-[11px] text-slate-400 block mt-0.5 font-normal">
                            📞 {t.clientPhone}
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-400 block mt-0.5 font-normal">
                            Direct / Walk-in
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md text-xs font-bold bg-orange-50 text-orange-700 border border-orange-200">
                            -{t.qtyOut} Units
                          </span>
                          <span className="text-xs font-semibold text-slate-700">
                            from <span className="text-slate-900 font-bold">{t.warehouse}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500">
                          {t.batchId && <span>Batch: {t.batchId} •</span>}
                          <span className="font-semibold text-blue-900">
                            Stock Bal: {t.runningBalance} Units
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <div className="font-bold text-slate-900 text-sm">
                          Total: {t.lineTotal ? `₹${Number(t.lineTotal).toLocaleString("en-IN")}` : "-"}
                        </div>
                        <div className="text-[11px] text-slate-500 font-medium mt-0.5">
                          Rate: {t.unitPrice ? `₹${Number(t.unitPrice).toLocaleString("en-IN")}/unit` : "-"}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center whitespace-nowrap">
                        <div className="flex flex-col gap-1 items-center justify-center">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider block w-fit ${
                              t.paymentStatus === "paid"
                                ? "bg-green-100 text-green-700"
                                : t.paymentStatus === "partial"
                                ? "bg-amber-100 text-amber-700"
                                : "bg-rose-100 text-rose-700"
                            }`}
                          >
                            {t.paymentStatus || "unpaid"}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider block w-fit ${
                              t.orderStatus === "completed" || t.orderStatus === "delivered"
                                ? "bg-blue-100 text-blue-700"
                                : t.orderStatus === "processing"
                                ? "bg-amber-100 text-amber-700"
                                : "bg-slate-100 text-slate-700"
                            }`}
                          >
                            {t.orderStatus || "delivered"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* Total Sales Summary Footer */}
            {ledgerData && (
              <tfoot>
                <tr className="bg-blue-50/90 border-t-2 border-blue-300 font-bold text-slate-900">
                  <td colSpan={3} className="py-3.5 px-4">
                    <div className="flex items-center gap-2.5">
                      <span className="font-bold text-xs uppercase tracking-wider text-[#172554]">
                        Total Period Sales
                      </span>
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-white text-[#172554] border border-blue-200 shadow-2xs">
                        {filteredSales.length} Orders
                      </span>
                    </div>
                  </td>
                  <td className="py-3.5 px-4">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-orange-100 text-orange-800 border border-orange-200 shadow-2xs">
                      -{salesTotalUnits} Units Sold
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-right whitespace-nowrap">
                    <span className="text-xs text-slate-500 font-medium mr-1.5">Total:</span>
                    <span className="font-mono font-bold text-sm text-[#172554]">
                      ₹
                      {filteredSales
                        .reduce((sum, t) => sum + Number(t.lineTotal || 0), 0)
                        .toLocaleString("en-IN")}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-center whitespace-nowrap">
                    <span className="inline-flex items-center text-[11px] font-semibold text-slate-700 bg-white px-2.5 py-1 rounded-md border border-blue-200 shadow-2xs">
                      Closing: <strong className="ml-1 text-slate-900">{ledgerData.closingStock} Units</strong>
                    </span>
                  </td>
                </tr>
              </tfoot>
            )}
          </ThemedTable>
        ) : (
          /* ================= 2. DEDICATED INVENTORY & STOCK ADJUSTMENTS TABLE ================= */
          <ThemedTable className="w-full text-[13px] table-fixed">
            <thead>
              <tr className="bg-[#172554] text-white uppercase tracking-wider text-[11px]">
                <th className="p-3 text-center w-10">#</th>
                <th className="p-3 text-left w-[22%]">Movement Details</th>
                <th className="p-3 text-left w-[19%]">Godown & Operator</th>
                <th className="p-3 text-left w-[15%]">Batch & Expiry</th>
                <th className="p-3 text-center w-[20%]">Qty & Stock Bal</th>
                <th className="p-3 text-left w-[24%]">Remarks / Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium text-slate-700 text-xs">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <Loader text="Loading inventory records..." />
                  </td>
                </tr>
              ) : filteredInventory.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-14 text-center text-slate-400 italic">
                    No inventory movements recorded for this period.
                  </td>
                </tr>
              ) : (
                filteredInventory.map((t, index) => {
                  return (
                    <tr key={t.id} className="hover:bg-slate-50/80 transition">
                      <td className="py-3 px-3 text-center text-slate-400 font-mono whitespace-nowrap">
                        {index + 1}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider inline-block ${
                              t.movementType === "return"
                                ? "bg-blue-50 text-blue-700 border border-blue-200"
                                : t.movementType === "transfer"
                                ? "bg-purple-50 text-purple-700 border border-purple-200"
                                : t.movementType === "deduction" || t.movementType === "dispose"
                                ? "bg-rose-50 text-rose-700 border border-rose-200"
                                : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            }`}
                          >
                            {t.typeLabel}
                          </span>
                          <span className="font-mono font-bold text-[#172554] text-xs">
                            {t.referenceId || "-"}
                          </span>
                        </div>
                        <span className="text-[11px] text-slate-400 block mt-1">
                          {format(new Date(t.date), "dd/MM/yyyy, hh:mm a")}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="text-slate-800 font-bold text-xs truncate" title={t.warehouse}>
                          {t.warehouse}
                        </div>
                        <div className="text-[11px] text-slate-500 font-medium mt-0.5 flex items-center gap-1 truncate">
                          <span>👤 {t.updatedBy || "Admin"}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="font-mono font-semibold text-slate-700 text-xs truncate">
                          {t.batchId ? `Batch: ${t.batchId}` : "Batch: N/A"}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                          {t.expiryDate ? `Exp: ${format(new Date(t.expiryDate), "dd MMM yyyy")}` : "No Expiry"}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center whitespace-nowrap">
                        <div>
                          {t.qtyIn > 0 ? (
                            <span className="inline-flex items-center justify-center min-w-[64px] px-2.5 py-0.5 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              +{t.qtyIn} Inward
                            </span>
                          ) : (
                            <span className="inline-flex items-center justify-center min-w-[64px] px-2.5 py-0.5 rounded-md text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
                              -{t.qtyOut} {t.movementType === "deduction" || t.movementType === "dispose" ? "Write-off" : "Outward"}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-600 font-mono font-bold mt-1">
                          Stock Bal: {t.runningBalance} Units
                        </div>
                      </td>
                      <td className="py-3 px-3 text-slate-600 text-xs">
                        <div className="truncate block" title={t.party || t.notes || "-"}>
                          {t.party || t.notes || "-"}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* Total Inventory Summary Footer */}
            {ledgerData && (
              <tfoot>
                <tr className="bg-blue-50/90 border-t-2 border-blue-300 font-bold text-slate-900">
                  <td colSpan={4} className="py-3 px-3">
                    <div className="flex items-center gap-2.5">
                      <span className="font-bold text-xs uppercase tracking-wider text-[#172554]">
                        Total Movements
                      </span>
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-white text-[#172554] border border-blue-200 shadow-2xs">
                        {filteredInventory.length} Entries
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-center whitespace-nowrap">
                    <div className="flex flex-col items-center gap-1">
                      {inventoryTransfersUnits > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-800 border border-blue-200 shadow-2xs">
                          ⇄ {inventoryTransfersUnits} Trf Moved
                        </span>
                      )}
                      {inventoryRestockUnits > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 shadow-2xs">
                          +{inventoryRestockUnits} Restock
                        </span>
                      )}
                      {inventoryWriteOffUnits > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-rose-100 text-rose-800 border border-rose-200 shadow-2xs">
                          -{inventoryWriteOffUnits} Write-off
                        </span>
                      )}
                      {inventoryTransfersUnits === 0 && inventoryRestockUnits === 0 && inventoryWriteOffUnits === 0 && (
                        <span className="text-slate-400 text-xs">0 Movements</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-center whitespace-nowrap">
                    <span className="inline-flex items-center text-[11px] font-semibold text-slate-700 bg-white px-2.5 py-1 rounded-md border border-blue-200 shadow-2xs">
                      Closing: <strong className="ml-1 text-slate-900">{ledgerData.closingStock} Units</strong>
                    </span>
                  </td>
                </tr>
              </tfoot>
            )}
          </ThemedTable>
        )}
      </div>
      </>
    )}
  </div>
  );
};

export default ProductStockLedger;
